import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import { Badge } from '@/components/ui/daisy';
import { WithClassname } from '@/components/ui/types';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import CoursePath from '@/models/CoursePath';
import { Breakpoints } from '@/utils/Breakpoint';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import ContPedStatus from './PedContentStatus';
import CourseFormatBadge from './CourseFormatBadge';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';
import { canDeleteCoursePath } from '@/utils/course-path/canDeleteCoursePath';

type PedContentCardProps = {
  coursePath: CoursePath;
  courseLineId: number;
  moduleId: number;
  actions?: ReactNode;
  onDelete?: (slug: string) => void;
  onCreateVersion?: (coursePath: CoursePath) => void;
  isDeleting?: boolean;
} & WithClassname;

export default function PedContentCard({
  coursePath,
  courseLineId,
  moduleId,
  actions,
  onDelete,
  onCreateVersion,
  isDeleting,
  className,
}: PedContentCardProps) {
  const { navigate } = useCourseTreeNavigate();
  const { t } = useTranslation('translation');
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });

  const handleEdit = () => {
    navigate(
      ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(
        courseLineId,
        moduleId,
        coursePath.slug,
      ),
      { treeKey: mountKey({ type: 'course', id: coursePath.slug }) },
    );
  };

  const canDelete = canDeleteCoursePath({ coursePath });

  const isPublished = coursePath.status === VersioningStatusEnum.PUBLISHED;

  const isBeta = coursePath.status === VersioningStatusEnum.BETA;

  const showMoreActions = canDelete || isPublished || isBeta;

  return (
    <CourseAdminCard isDeleting={isDeleting} className={className}>
      <CourseAdminCard.Header data-testid="course-path-card">
        <CourseAdminCard.Leading className="flex-col items-start">
          <div className="flex gap-4 items-center">
            <CourseAdminCard.Title>
              {coursePath.course.name}
            </CourseAdminCard.Title>
            <span
              data-testid="course-path-abbreviation"
              className="text-base-content max-xl:text-xs text-sm shrink-0"
            >
              {coursePath.course.abbreviation}
            </span>
            <ContPedStatus
              coursePath={coursePath}
              size={isXl ? 'sm' : 'xs'}
              showVersion
            />
          </div>
          <CourseAdminCard.Badges>
            {coursePath.course.formats.map((format, index) => (
              <CourseFormatBadge key={`format-${index}`} format={format} />
            ))}
            {coursePath.course.businessModels.map(model => (
              <Badge key={model} color="default">
                {t(`businessModels.${model}`)}
              </Badge>
            ))}
          </CourseAdminCard.Badges>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          {actions ?? (
            <Fragment>
              <CourseAdminCard.EditButton onClick={handleEdit} />
              <ConditionalRenderer condition={showMoreActions}>
                <CourseAdminCard.MoreActions>
                  {onCreateVersion && isPublished && (
                    <CourseAdminCard.MoreActions.Versioning
                      onClick={() => onCreateVersion(coursePath)}
                    />
                  )}
                  {onCreateVersion && isBeta && (
                    <CourseAdminCard.MoreActions.Versioning
                      onClick={() => onCreateVersion(coursePath)}
                      text={t('common.backToVersioning')}
                    />
                  )}
                  {onDelete && canDelete && (
                    <CourseAdminCard.MoreActions.Delete
                      onClick={() => onDelete(coursePath.slug)}
                      text={t('common.discardVersion')}
                      confirm
                    />
                  )}
                </CourseAdminCard.MoreActions>
              </ConditionalRenderer>
            </Fragment>
          )}
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
    </CourseAdminCard>
  );
}
